import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, DownloadSimple, CircleNotch, File } from '@phosphor-icons/react';
import {
  documentFileUrl,
  fetchDocumentMeta,
  formatFileSize,
  formatUploadDate,
  downloadDocumentFile,
} from '../utils/documentsApi';

export function DocumentPreviewPage() {
  const { dealId, documentId } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    let revoked = false;
    let objectUrl = null;

    async function load() {
      setLoading(true);
      setError('');
      try {
        const doc = await fetchDocumentMeta(dealId, documentId);
        if (revoked) return;
        setMeta(doc);

        if (doc.previewSupported) {
          const res = await fetch(documentFileUrl(dealId, documentId), { credentials: 'include' });
          if (!res.ok) throw new Error('Could not load file');
          const blob = await res.blob();
          objectUrl = URL.createObjectURL(blob);
          if (!revoked) setBlobUrl(objectUrl);
        }
      } catch {
        if (!revoked) setError('Document not found or access denied');
      }
      if (!revoked) setLoading(false);
    }

    load();
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [dealId, documentId]);

  const handleDownload = useCallback(async () => {
    if (!meta) return;
    try {
      await downloadDocumentFile(dealId, documentId, meta.fileName);
    } catch {
      setError('Download failed');
    }
  }, [dealId, documentId, meta]);

  const goBack = useCallback(() => {
    navigate('/', { state: { openDealId: dealId } });
  }, [navigate, dealId]);

  const canPreview = meta?.previewSupported && blobUrl;
  const isPdf = meta?.mimeType === 'application/pdf';
  const isImage = meta?.mimeType?.startsWith('image/');

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col" data-testid="document-preview-page">
      <header className="bg-white border-b border-gray-200 px-3 py-2 flex items-center gap-3 shrink-0">
        <button
          type="button"
          onClick={goBack}
          className="flex items-center gap-1 text-[11px] text-gray-600 hover:text-gray-900"
          data-testid="document-preview-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to analysis
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-sm font-semibold text-gray-900 truncate">{meta?.fileName || 'Document'}</h1>
          {meta && (
            <p className="text-[10px] text-gray-400">
              {meta.fileType} · {formatFileSize(meta.fileSize)} · Uploaded {formatUploadDate(meta.uploadedAt)}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={handleDownload}
          disabled={!meta}
          className="flex items-center gap-1 text-[11px] px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          data-testid="document-preview-download"
        >
          <DownloadSimple className="w-4 h-4" />
          Download
        </button>
      </header>

      <main className="flex-1 p-3 overflow-auto">
        {loading && (
          <div className="flex items-center justify-center gap-2 py-20 text-gray-400 text-sm">
            <CircleNotch className="w-5 h-5 animate-spin" />
            Loading preview…
          </div>
        )}

        {error && (
          <div className="max-w-lg mx-auto bg-white border border-gray-200 rounded-lg p-6 text-center">
            <p className="text-sm text-red-600 mb-4">{error}</p>
            <button type="button" onClick={goBack} className="text-[11px] text-blue-600 hover:underline">
              Return to analysis
            </button>
          </div>
        )}

        {!loading && !error && meta && canPreview && isPdf && (
          <iframe
            src={blobUrl}
            title={meta.fileName}
            className="w-full h-[calc(100vh-80px)] bg-white border border-gray-200 rounded-lg"
            data-testid="document-preview-pdf"
          />
        )}

        {!loading && !error && meta && canPreview && isImage && (
          <div className="flex justify-center">
            <img
              src={blobUrl}
              alt={meta.fileName}
              className="max-w-full max-h-[calc(100vh-100px)] rounded-lg border border-gray-200 bg-white shadow-sm"
              data-testid="document-preview-image"
            />
          </div>
        )}

        {!loading && !error && meta && !canPreview && (
          <div className="max-w-lg mx-auto bg-white border border-gray-200 rounded-lg p-8 text-center" data-testid="document-preview-metadata">
            <File className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-sm font-medium text-gray-800 mb-1">{meta.fileName}</p>
            <p className="text-[11px] text-gray-500 mb-1">{meta.fileType} · {formatFileSize(meta.fileSize)}</p>
            <p className="text-[11px] text-gray-400 mb-4">
              Uploaded {formatUploadDate(meta.uploadedAt)}
              {meta.uploadedBy ? ` by ${meta.uploadedBy}` : ''}
            </p>
            <p className="text-[11px] text-gray-400 mb-4">Preview is not available for this file type.</p>
            <button
              type="button"
              onClick={handleDownload}
              className="inline-flex items-center gap-1 text-[11px] px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
            >
              <DownloadSimple className="w-4 h-4" />
              Download file
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
